import delve from 'dlv';
import { connect } from 'react-redux';
import { compose, withProps } from 'recompose';
import { connect as apiConnect, PromiseState } from 'react-refetch';
import * as Sentry from '@sentry/react';
import {
  apiTypeToEntityType,
  entityTypeToApiType,
} from 'helpers/mapEntityTypes';
import { RootState } from 'redux/rootReducer';
import { EntityTypes } from 'context/entityContext';
import { addEntities, shouldLoadEntity } from 'redux/reducers/entity';
import { removeMultipleFollowingEntities } from 'redux/reducers/user/following';
import getRuntimeEnv from './env';

const entityTypes = ['track', 'album', 'artist', 'label', 'project'];

// @TODO see if this can be simplified a bit (or a lot). Should these fetches happen in Redux maybe, so we can also manage loading and success states better?
// @TODO This also looks like a great use case for reselect and/or the normalization of our store.
const getFollowingEntitiesData = (Component: any): any =>
  compose(
    connect(
      (state: RootState) => ({
        loadedEntities: state.entity,
        followingEntities: state.userSettings.data.followingEntities,
      }),
      {
        addEntities,
        removeMultipleFollowingEntities,
        shouldLoadEntity,
      }
    ) as any,
    apiConnect((props: any) => {
      const {
        followingEntities,
        addEntities,
        removeMultipleFollowingEntities,
        shouldLoadEntity,
      } = props;
      const idsPerEntityType: any = {};
      (followingEntities && followingEntities.length > 0
        ? followingEntities
        : []
      ).forEach((item: any) => {
        /** Check if the followed item isn't already stored in Redux or has an error state */
        if (shouldLoadEntity(item.type, item.id)) {
          if (!idsPerEntityType[item.type]) {
            idsPerEntityType[item.type] = [];
          }

          /** Push the item to a list of entityIds that still need to be loaded */
          idsPerEntityType[item.type].push(item.id);
        }
      });

      const fetchObjects: any = {};
      entityTypes.forEach((type) => {
        if (idsPerEntityType[type]) {
          /** Create a fetch for every entityType that still needs to load entities */
          fetchObjects[`${type}Fetch`] = {
            url: `/api/${entityTypeToApiType(type as EntityTypes)}/byIds?expand=none`,
            method: 'POST',
            body: JSON.stringify(idsPerEntityType[type]),
            then: (entities: any): any => {
              /**
               * Make sure we filter away the items that return a 404.
               * @todo Instead of removing those items we should show a message to the user.
               */
              const actualEntities = entities.filter(
                (item: any) => delve(item, 'code') !== 404 && item.entity
              );
              const removedEntities = entities.filter(
                (item: any) => delve(item, 'code') === 404
              );

              if (actualEntities.length > 0) {
                addEntities(apiTypeToEntityType(type), actualEntities);
              }

              if (removedEntities.length > 0) {
                /** Sanitize the entities that are not in Opus anymore. */
                removeMultipleFollowingEntities(
                  type,
                  removedEntities.map(({ id }: any) => id)
                );
              }
            },
            catch: (reason: any, meta: any): any => {
              // Report the error to the console
              // eslint-disable-next-line no-console
              console.error(
                'An error occured during fetching following entities data',
                new Error(reason)
              );

              // Report the error to Sentry
              if (getRuntimeEnv().REACT_APP_SENTRY_DSN) {
                Sentry.withScope((scope) => {
                  scope.setExtras({ meta });
                  Sentry.captureException(reason);
                });
              }
            },
          };
        }
      });

      return fetchObjects;
    }) as any,
    withProps((props: any) => {
      /** Check which fetcher per entityType are created so that we can check the state of them combined */
      const usedFetches = entityTypes.reduce(
        (acc: any, type: any) =>
          props[`${type}Fetch`] ? [...acc, props[`${type}Fetch`]] : acc,
        []
      );

      const allFetches = PromiseState.all(usedFetches);

      /** Get all the entity data per followed entity from the entity reducer */
      const followingEntitiesData = (props.followingEntities &&
      props.followingEntities.length > 0
        ? props.followingEntities
        : []
      )
        .map((item: any) =>
          delve(
            props,
            `loadedEntities.byId.${apiTypeToEntityType(item.type)}.${item.id}`
          )
        )
        /** Make sure the item is entityData is there, so we do not have undefined values in the list */
        .filter((entity: any) => Boolean(entity));

      return {
        followingLoading: allFetches.pending,
        followingSuccess: allFetches.fulfilled,
        followingError: allFetches.rejected
          ? new Error(delve(allFetches, 'reason.cause.error'))
          : null,
        followingErrorInfo: allFetches.rejected
          ? delve(allFetches, 'reason.cause.message')
          : null,
        followingData: allFetches.fulfilled ? followingEntitiesData : [],
      };
    }) as any
  )(Component as any) as any;

export default getFollowingEntitiesData;
